import React, { useEffect, useState, Component, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore, useThemeStore } from './store/auth';
import { reportarErrorCliente } from './utils/errorReporting';
import { sincronizarSuscripcion } from './services/pushService';
import AvisoNotificaciones from './components/AvisoNotificaciones';
import Navbar from './components/Navbar';
import UpgradeModal from './components/UpgradeModal';
import TrialBanner from './components/TrialBanner';
import BannerGlobal from './components/BannerGlobal';
import Onboarding from './components/Onboarding';
import QuickAdd from './components/QuickAdd';

// Pages - Lazy loading
import Login from './pages/Login';
import Landing from './pages/Landing';
import InstallPrompt from './components/InstallPrompt';
import EspacioBanner from './components/EspacioBanner';
import { ToastHost } from './components/Toast';
import { ConfirmHost } from './components/ConfirmDialog';
import lazyConReintento from './utils/lazyConReintento';

// Páginas bajo demanda: la landing y el login cargan sin arrastrar el resto de la app
const Registro = lazyConReintento(() => import('./pages/Registro'));
const RecuperarContrasena = lazyConReintento(() => import('./pages/RecuperarContrasena'));
const VerificarEmail = lazyConReintento(() => import('./pages/VerificarEmail'));
const Planes = lazyConReintento(() => import('./pages/Planes'));
const ImportarCFDI = lazyConReintento(() => import('./pages/ImportarCFDI'));
const Dashboard = lazyConReintento(() => import('./pages/Dashboard'));
const Ingresos = lazyConReintento(() => import('./pages/Ingresos'));
const Gastos = lazyConReintento(() => import('./pages/Gastos'));
const Balance = lazyConReintento(() => import('./pages/Balance'));
const Metas = lazyConReintento(() => import('./pages/Metas'));
const Reportes = lazyConReintento(() => import('./pages/Reportes'));
const Perfil = lazyConReintento(() => import('./pages/Perfil'));
const TarjetasCredito = lazyConReintento(() => import('./pages/TarjetasCredito'));
const Busqueda = lazyConReintento(() => import('./pages/Busqueda'));
const Presupuestos = lazyConReintento(() => import('./pages/Presupuestos'));
const Deudas = lazyConReintento(() => import('./pages/Deudas'));
const Categorias = lazyConReintento(() => import('./pages/Categorias'));
const Importar = lazyConReintento(() => import('./pages/Importar'));
const IngresosAutomaticos = lazyConReintento(() => import('./pages/IngresosAutomaticos'));
const Documentacion = lazyConReintento(() => import('./pages/Documentacion'));
const Suscripciones = lazyConReintento(() => import('./pages/Suscripciones'));
const Admin = lazyConReintento(() => import('./pages/Admin'));
const PagoExitoso = lazyConReintento(() => import('./pages/PagoExitoso'));
const Recibo = lazyConReintento(() => import('./pages/Recibo'));
const PagoFallido = lazyConReintento(() => import('./pages/PagoFallido'));
const Privacidad = lazyConReintento(() => import('./pages/Privacidad'));
const Terminos = lazyConReintento(() => import('./pages/Terminos'));
const Seguridad = lazyConReintento(() => import('./pages/Seguridad'));
const Exportar = lazyConReintento(() => import('./pages/Exportar'));
const Calendario = lazyConReintento(() => import('./pages/Calendario'));
const CalculadoraMetas = lazyConReintento(() => import('./pages/CalculadoraMetas'));
const GastosCompartidos = lazyConReintento(() => import('./pages/GastosCompartidos'));
const Proyectos = lazyConReintento(() => import('./pages/Proyectos'));
const PatrimonioNeto = lazyConReintento(() => import('./pages/PatrimonioNeto'));
const Cuentas = lazyConReintento(() => import('./pages/Cuentas'));
const Transferencias = lazyConReintento(() => import('./pages/Transferencias'));
const Reglas = lazyConReintento(() => import('./pages/Reglas'));
const NoEncontrado = lazyConReintento(() => import('./pages/NoEncontrado'));
const Impuestos = lazyConReintento(() => import('./pages/Impuestos'));
const CalculadoraResico = lazyConReintento(() => import('./pages/CalculadoraResico'));
const CalculadoraGastosHormiga = lazyConReintento(() => import('./pages/CalculadoraGastosHormiga'));
const CalculadoraQuincena = lazyConReintento(() => import('./pages/CalculadoraQuincena'));
const Espacios = lazyConReintento(() => import('./pages/Espacios'));
const EspacioLectura = lazyConReintento(() => import('./pages/EspacioLectura'));
const Inversiones = lazyConReintento(() => import('./pages/Inversiones'));
const Asistente = lazyConReintento(() => import('./pages/Asistente'));
const Semana = lazyConReintento(() => import('./pages/Semana'));
const Insignias = lazyConReintento(() => import('./pages/Insignias'));

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  componentDidCatch(error, info) {
    reportarErrorCliente('js_boundary', error?.message, { trace: info?.componentStack });
  }
  render() {
    if (this.state.error) return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6">
        <div className="text-center max-w-md">
          <div className="text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Algo salió mal</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">{this.state.error?.message || 'Error inesperado'}</p>
          <button onClick={() => window.location.reload()}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition">
            Recargar app
          </button>
        </div>
      </div>
    );
    return this.props.children;
  }
}

// Protected Route Component
function ProtectedRoute({ children, adminOnly = false }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Cargando...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated && adminOnly && user?.rol !== 'admin') return <Navigate to="/dashboard" />;
  return isAuthenticated ? children : <Navigate to="/login" />;
}

export default function App() {
  const { getMe, isAuthenticated, user } = useAuthStore();
  const { initTheme } = useThemeStore();
  const [initialized, setInitialized] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [mantenimiento, setMantenimiento] = useState(null);

  useEffect(() => {
    const initializeApp = async () => {
      initTheme();
      await getMe();
      setInitialized(true);
    };
    initializeApp();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (e) => setMantenimiento(e.detail?.mensaje || 'El sistema está en mantenimiento.');
    window.addEventListener('modo-mantenimiento', handler);
    return () => window.removeEventListener('modo-mantenimiento', handler);
  }, []);

  // Captura errores JS que no pasan por el ErrorBoundary (event handlers, async, promesas)
  useEffect(() => {
    const onError = (e) => {
      reportarErrorCliente('js_error', e.message, { archivo: e.filename, linea: e.lineno, trace: e.error?.stack });
    };
    const onRejection = (e) => {
      const reason = e.reason;
      reportarErrorCliente('js_rejection', reason?.message || String(reason), { trace: reason?.stack });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  // Mostrar onboarding solo a usuarios nuevos (flag en DB)
  useEffect(() => {
    if (isAuthenticated && user && !user.onboarding_done) {
      setShowOnboarding(true);
    }
  }, [isAuthenticated, user]);

  // Si el permiso ya está concedido, mantener la suscripción al día. El permiso
  // en sí no se pide aquí: lo pide AvisoNotificaciones desde un clic, que es lo
  // que exige iOS y lo que evita que Chrome penalice el origen.
  useEffect(() => {
    if (!isAuthenticated) return;
    sincronizarSuscripcion();
  }, [isAuthenticated]);

  if (!initialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Iniciando sesión...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
    <Router>
      {/* Overlay de mantenimiento — cubre toda la app para usuarios autenticados */}
      {mantenimiento && isAuthenticated && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-slate-900/95 backdrop-blur-sm">
          <div className="text-center max-w-md px-6">
            <div className="text-6xl mb-6">🚧</div>
            <h1 className="text-2xl font-bold text-white mb-3">Sistema en mantenimiento</h1>
            <p className="text-gray-300 mb-6">{mantenimiento}</p>
            <button onClick={() => window.location.reload()}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition">
              Reintentar
            </button>
          </div>
        </div>
      )}
      {isAuthenticated && <Navbar />}
      {isAuthenticated && <BannerGlobal />}
      <ToastHost />
      <ConfirmHost />
      {isAuthenticated && <TrialBanner />}
      {isAuthenticated && <EspacioBanner />}
      {isAuthenticated && <AvisoNotificaciones />}
      <UpgradeModal />
      <InstallPrompt />
      {isAuthenticated && <QuickAdd />}
      {showOnboarding && isAuthenticated && (
        <Onboarding onComplete={() => setShowOnboarding(false)} />
      )}
      <div className={isAuthenticated ? 'con-fab' : 'bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800'}>
        <Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center text-gray-500 dark:text-gray-400">Cargando…</div>}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/registro" element={<Registro />} />
          <Route path="/recuperar" element={<RecuperarContrasena />} />
          <Route path="/verificar" element={<VerificarEmail />} />
          <Route path="/planes" element={<Planes />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ingresos"
            element={
              <ProtectedRoute>
                <Ingresos />
              </ProtectedRoute>
            }
          />

          <Route path="/ingresos/lista" element={<Navigate to="/ingresos" />} />

          <Route
            path="/gastos"
            element={
              <ProtectedRoute>
                <Gastos />
              </ProtectedRoute>
            }
          />

          <Route path="/gastos/lista" element={<Navigate to="/gastos" />} />

          <Route
            path="/balance"
            element={
              <ProtectedRoute>
                <Balance />
              </ProtectedRoute>
            }
          />

          <Route
            path="/metas"
            element={
              <ProtectedRoute>
                <Metas />
              </ProtectedRoute>
            }
          />

          <Route
            path="/reportes"
            element={
              <ProtectedRoute>
                <Reportes />
              </ProtectedRoute>
            }
          />

          <Route
            path="/perfil"
            element={
              <ProtectedRoute>
                <Perfil />
              </ProtectedRoute>
            }
          />

          <Route
            path="/tarjetas-credito"
            element={
              <ProtectedRoute>
                <TarjetasCredito />
              </ProtectedRoute>
            }
          />

          <Route
            path="/busqueda"
            element={
              <ProtectedRoute>
                <Busqueda />
              </ProtectedRoute>
            }
          />

          <Route
            path="/presupuestos"
            element={
              <ProtectedRoute>
                <Presupuestos />
              </ProtectedRoute>
            }
          />

          <Route
            path="/deudas"
            element={
              <ProtectedRoute>
                <Deudas />
              </ProtectedRoute>
            }
          />

          <Route
            path="/categorias"
            element={
              <ProtectedRoute>
                <Categorias />
              </ProtectedRoute>
            }
          />

          <Route
            path="/importar"
            element={
              <ProtectedRoute>
                <Importar />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ingresos-automaticos"
            element={
              <ProtectedRoute>
                <IngresosAutomaticos />
              </ProtectedRoute>
            }
          />

          <Route
            path="/documentacion"
            element={
              <ProtectedRoute adminOnly>
                <Documentacion />
              </ProtectedRoute>
            }
          />

          <Route path="/cfdi" element={<ProtectedRoute><ImportarCFDI /></ProtectedRoute>} />
          <Route path="/suscripciones" element={<ProtectedRoute><Suscripciones /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          <Route path="/exportar" element={<ProtectedRoute><Exportar /></ProtectedRoute>} />
          <Route path="/planes/pago-exitoso" element={<PagoExitoso />} />
          <Route path="/recibo/:id" element={<ProtectedRoute><Recibo /></ProtectedRoute>} />
          <Route path="/planes/pago-fallido" element={<PagoFallido />} />
          <Route path="/privacidad" element={<Privacidad />} />
          <Route path="/terminos" element={<Terminos />} />
          <Route path="/seguridad" element={<Seguridad />} />
<Route path="/calendario" element={<ProtectedRoute><Calendario /></ProtectedRoute>} />
          <Route path="/calculadora" element={<ProtectedRoute><CalculadoraMetas /></ProtectedRoute>} />
          <Route path="/compartidos" element={<ProtectedRoute><GastosCompartidos /></ProtectedRoute>} />
          <Route path="/proyectos" element={<ProtectedRoute><Proyectos /></ProtectedRoute>} />
          <Route path="/patrimonio" element={<ProtectedRoute><PatrimonioNeto /></ProtectedRoute>} />
          <Route path="/cuentas" element={<ProtectedRoute><Cuentas /></ProtectedRoute>} />
          <Route path="/transferencias" element={<ProtectedRoute><Transferencias /></ProtectedRoute>} />
          <Route path="/reglas" element={<ProtectedRoute><Reglas /></ProtectedRoute>} />
          <Route path="/impuestos" element={<ProtectedRoute><Impuestos /></ProtectedRoute>} />
          <Route path="/calculadora-resico" element={<CalculadoraResico />} />
          <Route path="/calculadora-gastos-hormiga" element={<CalculadoraGastosHormiga />} />
          <Route path="/calculadora-quincena" element={<CalculadoraQuincena />} />
          <Route path="/espacios" element={<ProtectedRoute><Espacios /></ProtectedRoute>} />
          <Route path="/espacio/lectura" element={<ProtectedRoute><EspacioLectura /></ProtectedRoute>} />
          <Route path="/inversiones" element={<ProtectedRoute><Inversiones /></ProtectedRoute>} />
          <Route path="/asistente" element={<ProtectedRoute><Asistente /></ProtectedRoute>} />
          <Route path="/semana" element={<ProtectedRoute><Semana /></ProtectedRoute>} />
          <Route path="/insignias" element={<ProtectedRoute><Insignias /></ProtectedRoute>} />
          <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" /> : <Landing />} />
          <Route path="*" element={<NoEncontrado />} />
        </Routes>
        </Suspense>
      </div>
    </Router>
    </ErrorBoundary>
  );
}
